import { useState, useCallback } from 'react';
import { toast } from '../components/toast';
import { useLoadingState } from './useLoadingState';
import { getFinanceStats } from '../services/api';

export const useFinanceDashboardOperations = () => {
  const { isLoading, withLoading } = useLoadingState({ minLoadingTime: 800 });

  const [financialStats, setFinancialStats] = useState({
    totalRevenue: 0,
    totalExpenses: 0,
    netProfit: 0,
    profitMargin: 0,
    cashFlow: 0,
    accountsReceivable: 0,
    accountsPayable: 0,
    workingCapital: 0,
  });

  const [monthlyData, setMonthlyData] = useState({
    revenue: [],
    expenses: [],
    profit: [],
    months: [],
  });

  const [expenseBreakdown, setExpenseBreakdown] = useState([]);

  const [revenueBreakdown, setRevenueBreakdown] = useState([]);

  const [cashFlowData, setCashFlowData] = useState([]);

  const [recentTransactions, setRecentTransactions] = useState([]);

  const [budgetData, setBudgetData] = useState({
    budgeted: [],
    actual: [],
    variance: [],
  });

  const [financialRatios, setFinancialRatios] = useState({
    currentRatio: 0,
    quickRatio: 0,
    debtToEquity: 0,
    returnOnInvestment: 0,
    grossProfitMargin: 0,
    operatingProfitMargin: 0,
  });

  const loadDashboardData = useCallback(
    async (startDate = null, endDate = null) => {
      await withLoading(async () => {
        try {
          const response = await getFinanceStats(startDate, endDate);
          const { data } = response;

          // Set financial stats
          setFinancialStats({
            totalRevenue: data.financialStats.totalRevenue || 0,
            totalExpenses: data.financialStats.totalExpenses || 0,
            netProfit: data.financialStats.netProfit || 0,
            profitMargin: data.financialStats.profitMargin || 0,
            cashFlow: 0, // Not provided by backend yet
            accountsReceivable: data.financialStats.accountsReceivable || 0,
            accountsPayable: 0, // Not provided by backend yet
            workingCapital: 0, // Not provided by backend yet
          });

          // Set monthly data
          setMonthlyData({
            revenue: data.monthlyData.revenue || [],
            expenses: data.monthlyData.expenses || [],
            profit: data.monthlyData.profit || [],
            months: data.monthlyData.months || [],
          });

          // Set expense breakdown
          const expenseColors = [
            '#FF6B6B',
            '#4ECDC4',
            '#45B7D1',
            '#96CEB4',
            '#FFEAA7',
            '#DDA0DD',
            '#98D8C8',
            '#F7DC6F',
            '#BB8FCE',
            '#85C1E2',
          ];
          const expenseData = (data.expenseBreakdown || []).map((expense, index) => ({
            category: expense.category,
            amount: expense.amount,
            percentage: expense.percentage,
            color: expenseColors[index % expenseColors.length],
          }));
          setExpenseBreakdown(expenseData);

          // Set revenue breakdown
          const revenueColors = ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#00BCD4', '#FF5722'];
          const revenueData = (data.revenueBreakdown || []).map((revenue, index) => ({
            source: revenue.source,
            amount: revenue.amount,
            percentage: revenue.percentage,
            color: revenueColors[index % revenueColors.length],
          }));
          setRevenueBreakdown(revenueData);

          // Generate cash flow data from monthly data
          const cashFlow = data.monthlyData.months.map((month, index) => ({
            month,
            inflow: data.monthlyData.revenue[index] || 0,
            outflow: data.monthlyData.expenses[index] || 0,
            net: data.monthlyData.profit[index] || 0,
          }));
          setCashFlowData(cashFlow);

          // Keep mock data for features not yet implemented in backend
          setRecentTransactions([
            {
              id: 1,
              type: 'income',
              description: 'Client Payment - Project Alpha',
              amount: 45000,
              date: '2024-01-15',
              status: 'completed',
            },
            {
              id: 2,
              type: 'expense',
              description: 'Office Rent Payment',
              amount: 8500,
              date: '2024-01-14',
              status: 'completed',
            },
            {
              id: 3,
              type: 'income',
              description: 'Consulting Fee - Beta Corp',
              amount: 12500,
              date: '2024-01-13',
              status: 'completed',
            },
            {
              id: 4,
              type: 'expense',
              description: 'Software License Renewal',
              amount: 3200,
              date: '2024-01-12',
              status: 'completed',
            },
            {
              id: 5,
              type: 'income',
              description: 'Product Sales Revenue',
              amount: 8500,
              date: '2024-01-11',
              status: 'pending',
            },
          ]);

          setBudgetData({
            budgeted: data.monthlyData.revenue.map((rev) => rev * 0.9),
            actual: data.monthlyData.revenue,
            variance: data.monthlyData.revenue.map((rev) => rev - rev * 0.9),
          });

          setFinancialRatios({
            currentRatio: 2.1,
            quickRatio: 1.4,
            debtToEquity: 0.3,
            returnOnInvestment: data.financialStats.profitMargin || 0,
            grossProfitMargin: 35.2,
            operatingProfitMargin: data.financialStats.profitMargin || 0,
          });
        } catch (error) {
          console.error('Error loading finance dashboard data:', error);
          toast.error('Failed to load finance dashboard data');
        }
      });
    },
    [withLoading]
  );

  const refreshStats = useCallback(async () => {
    try {
      await loadDashboardData();
    } catch (error) {
      console.error('Error refreshing finance stats:', error);
    }
  }, [loadDashboardData]);

  const getDashboardSummary = useCallback(() => {
    return {
      totalRevenue: financialStats.totalRevenue,
      totalExpenses: financialStats.totalExpenses,
      netProfit: financialStats.netProfit,
      profitMargin: financialStats.profitMargin,
      cashFlow: financialStats.cashFlow,
      accountsReceivable: financialStats.accountsReceivable,
      accountsPayable: financialStats.accountsPayable,
      workingCapital: financialStats.workingCapital,
    };
  }, [financialStats]);

  const exportFinancialReport = useCallback(
    async (format = 'pdf') => {
      await withLoading(async () => {
        try {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          toast.success(`Financial report exported as ${format.toUpperCase()}`);
        } catch (error) {
          console.error('Error exporting financial report:', error);
          toast.error('Failed to export financial report');
        }
      });
    },
    [withLoading]
  );

  return {
    isLoading,
    financialStats,
    monthlyData,
    expenseBreakdown,
    revenueBreakdown,
    cashFlowData,
    recentTransactions,
    budgetData,
    financialRatios,
    loadDashboardData,
    refreshStats,
    getDashboardSummary,
    exportFinancialReport,
  };
};
